import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { NewsService } from '../../../shared/services/news/news.service';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { CustomPipe } from '../../../shared/pipes/custom.pipe';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { MailTitlePipe } from '../../../shared/pipes/mail-title.pipe';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { UsersService } from '../../../shared/services/users/users.service';
import { TagsPipe } from '../../../shared/pipes/tags.pipe';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';

@Component({
  selector: 'app-new',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    CustomPipe,
    TitleComponent,
    MailTitlePipe,
    RouterOutlet,
    TagsPipe,
    LoaderComponent,
    QeqPipe,
  ],
  templateUrl: './new.component.html',
  styleUrls: ['./new.component.css', '../../../shared/styles/layout.css'],
})
export class NewComponent implements OnDestroy {
  title: string = 'Noticias';
  subtitle: string = 'Gestiona las noticias relacionadas con la empresa.';

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;

  programmed = new FormControl<string[]>([]);
  programmedList: string[] = ['Con comentarios', 'Sin comentarios'];
  programmedMap: { [key: string]: boolean } = {
    'Con comentarios': true,
    'Sin comentarios': false,
  };

  featured = new FormControl<string[]>([]);
  featuredList: string[] = ['Destacada', 'No destacada'];
  featuredMap: { [key: string]: boolean } = {
    Destacada: true,
    'No destacada': false,
  };

  get isLoading() {
    return this.loaderService.getLoader();
  }

  constructor(
    private userService: UsersService,
    private newsService: NewsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private storageService: StorageService
  ) {}

  selectedFilter: string = 'todo';

  filters = [
    { value: 'mes', label: 'Mes' },
    { value: 'semana', label: 'Últimos 7 días' },
    { value: 'hoy', label: 'Hoy' },
    { value: 'todo', label: 'Todo' },
  ];

  selectFilter(filter: string) {
    this.selectedFilter = filter;
    this.applyAllFilters();
  }

  // Helper methods for date filtering
  private getStartDateForFilter(filter: string): Date | null {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    switch (filter) {
      case 'mes':
        const monthStart = new Date(today);
        monthStart.setDate(1); // First day of current month
        return monthStart;

      case 'semana':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 6); // 7 days ago (including today)
        return weekStart;

      case 'hoy':
        return today;

      case 'todo':
      default:
        return null; // No date filtering
    }
  }

  // Method to apply all filters together
  applyAllFilters() {
    const programmedValues = this.getProgrammedFilterValues();
    const featuredValues = this.getFeaturedFilterValues();
    const startDate = this.getStartDateForFilter(this.selectedFilter);

    const filterValue = JSON.stringify({
      programmedFilter: programmedValues,
      featuredFilter: featuredValues,
      dateFilter: startDate ? startDate.getTime() : null, // Convert to timestamp for comparison
    });

    this.dataSource.filter = filterValue;
  }

  readonly dialog = inject(MatDialog);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = [
    'title',
    'author',
    'id',
    'published-date',
    'featured',
    'notifications',
    'allow_comments',
    'allow_reactions',
    'allow_images',
    'categoria',
    'tags',
    'acciones',
  ];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.setupCustomSorting();
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, nick: string) {
    // console.log('Abriendo diálogo para eliminar noticia ID:', id);

    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      disableClose: true, // Prevenir cierre accidental
      data: {
        title: '¿Seguro que quieres eliminar esta noticia?',
        message: nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          //console.log('Ejecutando handler para eliminar noticia ID:', id);
          const deleteObservable = this.newsService.deleteNews(id);
          //console.log('Observable creado para eliminar noticia ID:', id);
          return deleteObservable;
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar la tabla
    dialogRef.afterClosed().subscribe((confirmed) => {
      //console.log('Diálogo cerrado. Confirmado:', confirmed);
      if (confirmed) {
        this.dataSource.data = this.dataSource.data.filter(
          (news) => news.id !== id
        );
        // console.log('Noticia eliminada de la tabla local');
      }
    });
  }

  myroles: any = {}; // Change from array to object

  ngOnInit() {
    this.loaderService.showLoader();

    // Setup navigation listener
    this.setupNavigationListener();

    // Load roles and news data
    this.loadRoles();
    this.loadNewsData();

    // Setup filter subscriptions
    this.setupFilterSubscriptions();
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (
          this.router.url.includes('/communication/news/edit') ||
          this.router.url.includes('/communication/news/add')
        ) {
          this.isUuid = true;
        } else if (this.router.url.includes('/communication/news')) {
          this.isUuid = false;

          // Clear existing subscriptions before reloading data
          this.clearSubscriptions();

          // Reload roles and news data
          this.loadRoles();
          this.loadNewsData();
          this.setupFilterSubscriptions();
        }

        this.cdr.detectChanges();
      });
  }

  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'news',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      //console.log(
      //  'No user roles found in storage or no permissions for Comunicación'
      // );
    }
  }

  loadNewsData() {
    const newsSub = this.newsService.getNews().subscribe({
      next: (news) => {
        // Sort the news by publication_date (descending order)
        let filteredNews = news.sort((a, b) => {
          const dateA = a.publication_date
            ? new Date(a.publication_date).getTime()
            : new Date(a.created_when).getTime();
          const dateB = b.publication_date
            ? new Date(b.publication_date).getTime()
            : new Date(b.created_when).getTime();
          return dateB - dateA; // Descending order (most recent first)
        });

        //console.debug('filteredNews', filteredNews);
        this.dataSource = new MatTableDataSource(filteredNews);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        // Set up custom sorting
        this.setupCustomSorting();

        // Set up the filter predicate for programmed, featured, and date filtering
        this.dataSource.filterPredicate = (data, filter) => {
          const filterObj = JSON.parse(filter);
          let programmedMatch = true;
          let featuredMatch = true;
          let dateMatch = true;

          // Check programmed filter (whether comments are allowed)
          // Note: 'allow_comments' field might not exist in new API, so we default to true
          if (
            filterObj.programmedFilter &&
            filterObj.programmedFilter.length > 0
          ) {
            programmedMatch = filterObj.programmedFilter.includes(
              data.allow_comments !== undefined ? data.allow_comments : true
            );
          }

          // Check featured filter
          if (filterObj.featuredFilter && filterObj.featuredFilter.length > 0) {
            featuredMatch = filterObj.featuredFilter.includes(data.featured);
          }

          // Check date filter
          if (filterObj.dateFilter) {
            const createdWhen = new Date(data.created_when).getTime();
            dateMatch = createdWhen >= filterObj.dateFilter;
          }

          // Item passes filter only if it matches all conditions
          return programmedMatch && featuredMatch && dateMatch;
        };

        this.loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Error fetching news', error);
        this.loaderService.hideLoader();
      },
    });

    this.subscriptions.push(newsSub);
  }

  setupFilterSubscriptions() {
    // Subscribe to changes in the programmed control
    const programmedSub = this.programmed.valueChanges.subscribe(() => {
      this.applyAllFilters();
    });

    this.subscriptions.push(programmedSub);

    // Subscribe to changes in the featured control
    const featuredSub = this.featured.valueChanges.subscribe(() => {
      this.applyAllFilters();
    });

    this.subscriptions.push(featuredSub);
  }

  clearSubscriptions() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Reset the subscriptions array
    this.subscriptions = [];
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.clearSubscriptions();

    // Also clean up navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
  }

  // Set up custom sort functions for the table columns
  private setupCustomSorting() {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'published-date':
          // Sort by publication_date if it exists, otherwise by created_when
          return item.publication_date
            ? new Date(item.publication_date).getTime()
            : new Date(item.created_when).getTime();

        case 'featured':
          // Sort by boolean value (true first)
          return item.featured ? 0 : 1;

        case 'comentarios':
          // Sort by boolean value (true first)
          return item.allow_comments ? 0 : 1;

        case 'categoria':
          // Sort alphabetically by category name
          return item.category ? item.category.toLowerCase() : '';

        case 'tags':
          // Sort by number of tags
          return item.tags ? item.tags.length : 0;

        default:
          return item[property];
      }
    };
  }

  // Helper method to get current programmed filter values
  private getProgrammedFilterValues(): boolean[] {
    const selectedProgrammed = this.programmed.value;
    if (!selectedProgrammed || selectedProgrammed.length === 0) {
      return [];
    }
    return selectedProgrammed.map((progName) => this.programmedMap[progName]);
  }

  // Helper method to get current featured filter values
  private getFeaturedFilterValues(): boolean[] {
    const selectedFeatured = this.featured.value;
    if (!selectedFeatured || selectedFeatured.length === 0) {
      return [];
    }
    return selectedFeatured.map((featName) => this.featuredMap[featName]);
  }

  isUuid: boolean = false;
  goToMail(element: any) {
    this.router.navigate(['home/communication/news/edit', element.id]);
    this.isUuid = true;
  }

  goToNewMail() {
    this.router.navigate(['home/communication/news/add']);
    this.isUuid = true;
  }
}
